/**
 * EN-1570: helpers for the pig run "User Notifications" list.
 *
 * The Add User Notification dialog used to change only a local list, and the
 * Notifications dialog needed a second Save to send it to
 * PUT /pigrun/{id}/notifications. Now the add (or delete) saves straight
 * away, so these helpers build the list to save without changing the list
 * on screen until the save has succeeded.
 */

import { UserNotification } from '@/models/UserNotification';

/**
 * A new list with `notification` added. A user has at most one entry, so an
 * existing entry for the same user is replaced in place.
 */
export function upsertUserNotification(
  list: readonly UserNotification[],
  notification: UserNotification
): UserNotification[] {
  const idx = list.findIndex((n) => n.userId === notification.userId);
  if (idx < 0) return [...list, notification];
  const next = [...list];
  next[idx] = notification;
  return next;
}

/** A new list without the entry for `userId`. */
export function removeUserNotification(
  list: readonly UserNotification[],
  userId: string
): UserNotification[] {
  return list.filter((n) => n.userId !== userId);
}

export const SAVE_USER_NOTIFICATION_FAILED =
  'The notification could not be saved. Please try again.';

export const DELETE_USER_NOTIFICATION_FAILED =
  'The notification could not be removed. Please try again.';

/**
 * The message to show when saving the notification list fails. The API
 * answers 400 with a plain-text reason (for example an unknown user);
 * anything else gets `fallback`.
 */
export function userNotificationSaveErrorMessage(
  error: unknown,
  fallback: string = SAVE_USER_NOTIFICATION_FAILED
): string {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | null
  )?.response;
  if (
    response?.status === 400 &&
    typeof response.data === 'string' &&
    response.data.trim() !== ''
  ) {
    return response.data.trim();
  }
  return fallback;
}

export type SaveUserNotifications = (
  notifications: UserNotification[]
) => Promise<void>;

export type SaveUserNotificationsResult =
  | { ok: true; list: UserNotification[] }
  | { ok: false; error: string };

/**
 * Saves `next` with `save` (when given). On success the caller shows `next`;
 * on failure it keeps its current list and shows `error`. Without `save` the
 * change is local only (the old two-step behaviour).
 */
export async function saveUserNotificationList(
  next: UserNotification[],
  save: SaveUserNotifications | null | undefined,
  fallbackError: string = SAVE_USER_NOTIFICATION_FAILED
): Promise<SaveUserNotificationsResult> {
  if (save) {
    try {
      await save(next);
    } catch (err) {
      console.error('Failed to save notifications:', err);
      return {
        ok: false,
        error: userNotificationSaveErrorMessage(err, fallbackError),
      };
    }
  }
  return { ok: true, list: next };
}

export const LOAD_USER_NOTIFICATIONS_FAILED =
  'The notifications for this pig run could not be loaded. Please try again.';

export const USER_NOTIFICATIONS_NOT_LOADED =
  'The notification list is not loaded. Close and reopen Notifications.';

/**
 * Saving replaces the pig run's whole list (PUT), so it is only safe when the
 * list on screen was loaded from the server for this same pig run. Otherwise a
 * failed or stale load would wipe everyone's notifications.
 */
export function canSaveUserNotifications(
  loadedForPigRunId: number | null | undefined,
  pigRunId: number | null | undefined
): boolean {
  return (
    loadedForPigRunId !== null &&
    loadedForPigRunId !== undefined &&
    loadedForPigRunId === pigRunId
  );
}
